import React, { useEffect, useMemo, useState } from "react";
import cs from "classnames";
import styles from "./styles.module.scss";
import {
  RadioActiveIcon,
  RadioIdleIcon,
  AccordionChevronIcon,
  CalendarIcon,
  CollapseIcon,
  ExpandIcon,
  InfoIcon,
} from "./icons";
import { TimeDropdown } from "../TimeDropdown";
import { NewOrderHeader } from "../NewOrderHeader";
import {
  DailyLimitRowData,
  DailyLimitsMode,
  NewOrderDailyLimitsCardProps,
  NewOrderDailyLimitsProps,
} from "./types";

const formatTotal = (value: number): string =>
  Number.isInteger(value) ? String(value) : value.toFixed(2);

/**
 * The expanded "2. Daily limits" card of the new-order flow (Figma 1052:8958):
 * brand-outlined accordion with Fixed/Custom pills and the per-day limits
 * table (date, installs, start/end time, total), closed by a "Total + Done"
 * row. Rendered between the host screen's own header and footer so entering
 * fullscreen only swaps the middle content — nothing jumps. Pure
 * presentational — rows and handlers come from props; edits live in internal
 * state and are reported via onRowsChange.
 */
export const NewOrderDailyLimitsCard: React.FC<
  NewOrderDailyLimitsCardProps
> = ({
  variant = "fullscreen",
  collapsed = false,
  stepTitle = "2. Daily limits",
  onTitleToggle,
  mode = "custom",
  onModeChange,
  customDisabled = false,
  installsLimits,
  firstRowTimeLocked = false,
  fixedTimeLocked = false,
  rows,
  onRowsChange,
  pricePerInstall = 1.5,
  onFullscreenToggle,
  onDone,
  className,
}) => {
  const [limitMode, setLimitMode] = useState<DailyLimitsMode>(mode);
  const [limitRows, setLimitRows] = useState<DailyLimitRowData[]>(rows);

  useEffect(() => setLimitMode(mode), [mode]);
  useEffect(() => setLimitRows(rows), [rows]);

  const changeMode = (next: DailyLimitsMode) => {
    setLimitMode(next);
    onModeChange?.(next);
  };

  const updateRows = (next: DailyLimitRowData[]) => {
    setLimitRows(next);
    onRowsChange?.(next);
  };

  const updateRow = (index: number, patch: Partial<DailyLimitRowData>) => {
    updateRows(
      limitRows.map((row, i) => (i === index ? { ...row, ...patch } : row)),
    );
  };

  /** Fixed mode edits every row at once — the first row holds shared values. */
  const updateAllRows = (patch: Partial<DailyLimitRowData>) => {
    updateRows(limitRows.map((row) => ({ ...row, ...patch })));
  };

  const stepTotal = useMemo(
    () =>
      limitRows.reduce((sum, row) => sum + row.installs * pricePerInstall, 0),
    [limitRows, pricePerInstall],
  );

  const fixedRow = limitRows[0];

  // Digits only, max 5 chars, leading zeros stripped by parseInt.
  const handleInstallsInput = (raw: string): number => {
    const digits = raw.replace(/\D/g, "").slice(0, 5);
    return digits === "" ? 0 : parseInt(digits, 10);
  };

  // Fixed-mode min — shown in the Installs info tooltip (Figma 1096:99102,
  // fixed mode only). Null without installsLimits (DirectOrder).
  const minInstalls = installsLimits ? installsLimits.fixedMin : null;

  // First violation (fixed mode checks the shared row) — blocks Done. The min
  // rule lives in the Installs tooltip, so only the max error renders inline.
  const validationError = useMemo(() => {
    if (!installsLimits) return null;
    const check = (installs: number): string | null => {
      const min =
        limitMode === "fixed"
          ? installsLimits.fixedMin
          : installsLimits.customMin;
      if (installs < min) return `Minimum number of installs: ${min}`;
      if (installs > installsLimits.max)
        return `Maximum number of installs: ${installsLimits.max}`;
      return null;
    };
    if (limitMode === "fixed") {
      return fixedRow ? check(fixedRow.installs) : null;
    }
    for (const row of limitRows) {
      const error = check(row.installs);
      if (error) return error;
    }
    return null;
  }, [installsLimits, limitMode, limitRows, fixedRow]);

  const doneDisabled =
    !limitRows.some((row) => row.installs > 0) || !!validationError;

  if (collapsed) {
    const totalInstalls = limitRows.reduce((sum, row) => sum + row.installs, 0);
    const money = `$${formatTotal(stepTotal)}`;
    return (
      <div
        className={cs(styles.limitsCard, styles.limitsCardCollapsed, className)}
      >
        <div className={styles.collapsedTitleCol}>
          {onTitleToggle ? (
            <button
              type="button"
              className={cs(
                styles.stepToggle,
                styles.stepToggleButton,
                styles.stepToggleCollapsed,
              )}
              onClick={onTitleToggle}
              aria-label="Expand step"
            >
              <AccordionChevronIcon className={styles.accordionChevron} />
            </button>
          ) : (
            <span
              className={cs(styles.stepToggle, styles.stepToggleCollapsed)}
              aria-hidden
            >
              <AccordionChevronIcon className={styles.accordionChevron} />
            </span>
          )}
          <h3 className={styles.stepTitle}>{stepTitle}</h3>
        </div>

        <div className={styles.collapsedSummary}>
          <span className={styles.collapsedCaption}>Installs</span>
          <span className={styles.collapsedValue}>{totalInstalls}</span>
          <span className={styles.collapsedCaption}>= {money}</span>
        </div>

        <span className={styles.collapsedTotal}>{money}</span>
      </div>
    );
  }

  return (
    <div
      className={cs(
        styles.limitsCard,
        { [styles.limitsCardInline]: variant === "inline" },
        className,
      )}
    >
      <div className={styles.setupArea}>
        <div className={styles.startCol}>
          <div className={styles.accordionTitle}>
            {onTitleToggle ? (
              <button
                type="button"
                className={cs(styles.stepToggle, styles.stepToggleButton)}
                onClick={onTitleToggle}
                aria-label="Collapse step"
              >
                <AccordionChevronIcon className={styles.accordionChevron} />
              </button>
            ) : (
              <span className={styles.stepToggle} aria-hidden>
                <AccordionChevronIcon className={styles.accordionChevron} />
              </span>
            )}
            <h3 className={styles.stepTitle}>{stepTitle}</h3>
          </div>

          <div className={styles.modePills}>
            <button
              type="button"
              className={cs(styles.modePill, {
                [styles.modePillActive]: limitMode === "fixed",
              })}
              onClick={() => changeMode("fixed")}
              aria-pressed={limitMode === "fixed"}
            >
              {limitMode === "fixed" ? (
                <RadioActiveIcon className={styles.modeRadio} />
              ) : (
                <RadioIdleIcon className={cs(styles.modeRadio, styles.idle)} />
              )}
              Fixed
            </button>
            <button
              type="button"
              className={cs(styles.modePill, {
                [styles.modePillActive]: limitMode === "custom",
                [styles.modePillDisabled]: customDisabled,
              })}
              onClick={() => !customDisabled && changeMode("custom")}
              disabled={customDisabled}
              aria-pressed={limitMode === "custom"}
            >
              {limitMode === "custom" ? (
                <RadioActiveIcon className={styles.modeRadio} />
              ) : (
                <RadioIdleIcon className={cs(styles.modeRadio, styles.idle)} />
              )}
              Custom
            </button>
          </div>
        </div>

        {limitMode === "custom" ? (
          <div className={cs(styles.tableCol, styles.tableColDays)}>
            <div className={styles.tableRow}>
              <div className={styles.cells}>
                <span className={cs(styles.cell, styles.cellLabel)}>Date</span>
                <span className={cs(styles.cell, styles.cellLabel)}>
                  Installs
                </span>
                <span className={cs(styles.cell, styles.cellLabel)}>
                  Start time
                </span>
                <span className={cs(styles.cell, styles.cellLabel)}>
                  End time
                </span>
              </div>
              <span className={cs(styles.totalCol, styles.totalHeader)}>
                Total
              </span>
              <div className={styles.rail}>
                <button
                  type="button"
                  className={styles.collapseButton}
                  onClick={onFullscreenToggle}
                  aria-label={
                    variant === "inline"
                      ? "Enter fullscreen"
                      : "Exit fullscreen"
                  }
                >
                  {/* Compact card carries the Collapse glyph, fullscreen the
                      Expand one (Figma 722:89752 / 722:89873). */}
                  {variant === "inline" ? (
                    <CollapseIcon className={styles.collapseIcon} />
                  ) : (
                    <ExpandIcon className={styles.collapseIcon} />
                  )}
                </button>
              </div>
            </div>

            <div className={styles.rows}>
              {limitRows.map((row, index) => (
                <div key={row.date} className={styles.tableRow}>
                  <div className={styles.cells}>
                    <span className={cs(styles.cell, styles.dateCell)}>
                      <CalendarIcon className={styles.calendarIcon} />
                      {row.date}
                    </span>
                    <span className={styles.cell}>
                      <input
                        type="text"
                        inputMode="numeric"
                        className={styles.installsInput}
                        value={String(row.installs)}
                        onChange={(e) =>
                          updateRow(index, {
                            installs: handleInstallsInput(e.target.value),
                          })
                        }
                      />
                    </span>
                    <span className={styles.cell}>
                      <TimeDropdown
                        value={row.startTime}
                        onChange={(time) =>
                          updateRow(index, { startTime: time })
                        }
                        disabled={firstRowTimeLocked && index === 0}
                      />
                    </span>
                    <span className={styles.cell}>
                      <TimeDropdown
                        value={row.endTime}
                        onChange={(time) => updateRow(index, { endTime: time })}
                        disabled={firstRowTimeLocked && index === 0}
                      />
                    </span>
                  </div>
                  <span className={cs(styles.totalCol, styles.rowTotal)}>
                    {formatTotal(row.installs * pricePerInstall)}
                  </span>
                  <div className={styles.rail} />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className={styles.tableCol}>
            <div className={styles.tableRow}>
              <div className={styles.cells}>
                <span className={cs(styles.cell, styles.cellLabel)}>
                  Installs
                  {minInstalls !== null && (
                    <span className={styles.infoIconWrap}>
                      <InfoIcon className={styles.infoIcon} />
                      <span className={styles.infoTooltip} role="tooltip">
                        Minimum number of installs: {minInstalls}
                      </span>
                    </span>
                  )}
                </span>
                <span className={cs(styles.cell, styles.cellLabel)}>
                  Start time
                </span>
                <span className={cs(styles.cell, styles.cellLabel)}>
                  End time
                </span>
                <span className={styles.cell} />
              </div>
              <span className={cs(styles.totalCol, styles.totalHeader)}>
                Total
              </span>
              <div className={styles.rail} />
            </div>
            {fixedRow && (
              <div className={styles.tableRow}>
                <div className={styles.cells}>
                  <span className={styles.cell}>
                    <input
                      type="text"
                      inputMode="numeric"
                      className={styles.installsInput}
                      value={String(fixedRow.installs)}
                      onChange={(e) =>
                        updateAllRows({
                          installs: handleInstallsInput(e.target.value),
                        })
                      }
                    />
                  </span>
                  <span className={styles.cell}>
                    <TimeDropdown
                      value={fixedRow.startTime}
                      onChange={(time) => updateAllRows({ startTime: time })}
                      disabled={fixedTimeLocked}
                    />
                  </span>
                  <span className={styles.cell}>
                    <TimeDropdown
                      value={fixedRow.endTime}
                      onChange={(time) => updateAllRows({ endTime: time })}
                      disabled={fixedTimeLocked}
                    />
                  </span>
                  <span className={styles.cell} />
                </div>
                <span className={cs(styles.totalCol, styles.rowTotal)}>
                  {formatTotal(fixedRow.installs * pricePerInstall)}
                </span>
                <div className={styles.rail} />
              </div>
            )}
          </div>
        )}
      </div>

      <div
        className={cs(styles.confirmRow, {
          [styles.confirmRowInline]: variant === "inline",
        })}
      >
        {validationError?.startsWith("Maximum") && (
          <span className={styles.errorText}>{validationError}</span>
        )}
        {variant === "fullscreen" && (
          <span className={styles.stepTotalText}>
            Total: {formatTotal(stepTotal)}
          </span>
        )}
        <div className={styles.doneWrap}>
          <button
            type="button"
            className={styles.doneButton}
            disabled={doneDisabled}
            onClick={onDone}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * New-order step 2 "Daily limits" fullscreen (Figma 1052:8958). Header row
 * (title + order-type tag, completed/active stepper, wallet + theme switch +
 * close) over the expanded NewOrderDailyLimitsCard, then the Back / Total
 * budget / Create order footer. The card alone is exported separately for
 * hosts that already render their own header and footer.
 */
export const NewOrderDailyLimits: React.FC<NewOrderDailyLimitsProps> = ({
  orderType,
  tariffLabel,
  tariffLevel = "trial",
  tariffMeta,
  amount,
  onAddFund,
  theme = "light",
  onThemeChange,
  onClose,
  stepTitle,
  mode,
  onModeChange,
  customDisabled,
  installsLimits,
  firstRowTimeLocked,
  fixedTimeLocked,
  rows,
  onRowsChange,
  pricePerInstall,
  onFullscreenToggle,
  onDone,
  totalBudget,
  budgetFormula,
  totalInstalls,
  createOrderDisabled = true,
  onBack,
  onCreateOrder,
  fullscreen = false,
  className,
}) => {
  return (
    <div
      className={cs(
        styles.screen,
        { [styles.fullscreenOverlay]: fullscreen },
        className,
      )}
    >
      <NewOrderHeader
        orderType={orderType}
        steps={[
          { label: "Select an App", state: "completed" },
          { label: "Configure", state: "active" },
        ]}
        tariffLabel={tariffLabel}
        tariffLevel={tariffLevel}
        tariffMeta={tariffMeta}
        amount={amount}
        onAddFund={onAddFund}
        theme={theme}
        onThemeChange={onThemeChange}
        onClose={onClose}
      />

      <NewOrderDailyLimitsCard
        stepTitle={stepTitle}
        mode={mode}
        onModeChange={onModeChange}
        customDisabled={customDisabled}
        installsLimits={installsLimits}
        firstRowTimeLocked={firstRowTimeLocked}
        fixedTimeLocked={fixedTimeLocked}
        rows={rows}
        onRowsChange={onRowsChange}
        pricePerInstall={pricePerInstall}
        onFullscreenToggle={onFullscreenToggle}
        onDone={onDone}
      />

      <div className={styles.footer}>
        <button type="button" className={styles.backButton} onClick={onBack}>
          Back
        </button>

        <div className={styles.footerSummary}>
          <span className={styles.budgetGroup}>
            <span className={styles.budgetLabel}>Total budget:</span>
            <span className={styles.budgetValue}>{totalBudget}</span>
            {budgetFormula && (
              <span className={styles.budgetFormula}>({budgetFormula})</span>
            )}
          </span>
          <span className={styles.installsGroup}>
            <span className={styles.budgetLabel}>Total installs:</span>
            <span className={styles.installsValue}>{totalInstalls}</span>
          </span>
        </div>

        <button
          type="button"
          className={styles.createButton}
          disabled={createOrderDisabled}
          onClick={onCreateOrder}
        >
          Create order
        </button>
      </div>
    </div>
  );
};

export default NewOrderDailyLimits;
